/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext, whenCorporateView } from '@gh/builder-shared';
import { FormState, FormStateBuilder, getRouteInitData, updateSchema } from '@gh/common-shared';
import { getSlicedData, observableBasedSlicedDataSource, sliceDataByQuery } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  getControl,
  getControlValue,
  setControlValue,
  TinymceSettings,
  tmSettingsBuilder,
} from '@gh/core-ui';
import { includesIgnoreCase, isNotNil } from '@gh/core-util';
import { map$, switchMap$ } from '@gh/rx/operators';
import { first } from 'lodash';
import { Observable } from 'rxjs';
import { ReportFyaDetailsBusinessService } from '../../../resources/report-fya-details-business.service';
import {
  ReportFyaKeyGoalFilter,
  ReportFyaMasterZone,
} from '../../../resources/report-fya-details.model';
import { ReportFyaDetailsCommandService } from '../report-fya-details-command.service';
import { ReportFyaDetailsFormState } from '../report-fya-details-form.service';

@Component({
  standalone: false,
  selector: 'gh-report-fya-key-goals-tab',
  templateUrl: 'report-fya-key-goals-tab.component.html',
  styleUrls: ['report-fya-key-goals-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportFyaKeyGoalsTabComponent implements OnInit {
  masterZones: ReportFyaMasterZone[];

  filterForm: FormState<ReportFyaKeyGoalFilter>;

  tmSettings: TinymceSettings;

  isOfficeSelected$: Observable<boolean>;
  keyGoal$: Observable<string>;
  comments$: Observable<string>;

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              private formStateBuilder: FormStateBuilder,
              private form: ReportFyaDetailsFormState,
              private commands: ReportFyaDetailsCommandService,
              private business: ReportFyaDetailsBusinessService) {
  }

  ngOnInit(): void {
    const { masterZones } = getRouteInitData(this.route);

    const filter = {
      masterZone: this.userContext.masterView ? first(masterZones) : void 0,
    };

    this.masterZones = masterZones;

    this.filterForm = this.formStateBuilder.buildPlain({
      value: filter,
      children: {
        masterZone: { enabled: whenCorporateView() },
        office: { enabled: isNotNil(filter.masterZone) },
      },
    });

    const selectedOffice$ = getControl(this.filterForm.group, 'office').valueChanges;
    this.isOfficeSelected$ = selectedOffice$.pipe(map$(isNotNil));
    this.keyGoal$ = selectedOffice$.pipe(switchMap$((office) => office ? this.form.keyGoalForOffice$(office.id) : ['']));
    this.comments$ = selectedOffice$.pipe(switchMap$((office) => office ? this.form.commentsForOffice$(office.id) : ['']));

    this.tmSettings = tmSettingsBuilder()
      .styles(`
        .mce-content-body {
          font-family: Arial;
          font-size: 10px;
          line-height: 1;
        }
        p {
          margin: 0;
        }
      `)
      .fontSizes('10px 13px 15px 18px 24px 36px')
      .formats({
        italic: { inline: 'span', styles: { 'font-style': 'italic' } },
        bold: { inline: 'span', styles: { 'font-weight': 'bold' } },
        underline: { inline: 'span', styles: { 'text-decoration': 'underline' } },
      })
      .toolbarOptions({
        styleSelect: false,
        alignments: false,
        links: false,
        lists: true,
      })
      .scayt()
      .resizable(300, 400)
      .build();
  }

  onMasterZoneChange(): void {
    const mz = getControlValue(this.filterForm.group, 'masterZone');
    updateSchema(getControl(this.filterForm.group, 'office'), { enabled: isNotNil(mz) });
    setControlValue(this.filterForm.group, 'office', void 0);
  }

  onKeyGoalChange(content: string): void {
    const office = getControlValue(this.filterForm.group, 'office');
    this.commands.setKeyGoal(office.id, content);
  }

  onCommentsChange(content: string): void {
    const office = getControlValue(this.filterForm.group, 'office');
    this.commands.setComments(office.id, content);
  }

  loadMasterZones({ query, load }: AutocompleteLoadMoreEvent): void {
    load(sliceDataByQuery(this.masterZones.filter(({ name }) => includesIgnoreCase(name, query.filter)), query));
  }

  loadOffices({ query, load }: AutocompleteLoadMoreEvent): void {
    const mz = getControlValue(this.filterForm.group, 'masterZone');
    load(observableBasedSlicedDataSource(this.business.findAllOfficesByMasterZone(mz.id, query.filter))
      .select(query).pipe(
        map$(getSlicedData)));
  }
}
